import { create } from 'zustand';
import type { VaultData, VaultContact, VaultContactGroup, VaultContactIdentity } from '@shared/types';
import { saveLocalVault, clearLocalVault } from '../crypto/storage';
import { serverKeyFromServerUrl, hostFromServerUrl } from '../utils/serverKey';
import { useAuthStore } from './authStore';
import { syncContactToBridge, deleteContactFromBridge, syncAllContactsToBridge } from '../utils/webviewBridge';

let persistTimer: number | null = null;
function schedulePersist(vault: VaultData | null, revisionsByIdentityId: Record<string, number>, syncPending = false) {
  if (!vault) return;
  if (persistTimer) window.clearTimeout(persistTimer);
  persistTimer = window.setTimeout(() => {
    void saveLocalVault(vault, revisionsByIdentityId, syncPending);
  }, 250);
}

/**
 * Vault Store
 *
 * Single vault aggregates contacts from all identities/servers.
 * Each contact can have identities on multiple servers (e.g. a contact
 * known on both work and personal circles). When syncing, vaultSync
 * filters contacts per-server so each server only sees its own subset.
 */
interface VaultState {
  // Current vault data (decrypted)
  vaultData: VaultData | null;
  /** Vault revision per identityId. Keyed by identityId (not serverId) because
   *  the server stores one vault per identity, so revisions are per-identity. */
  revisionsByIdentityId: Record<string, number>;
  isSyncDirty: boolean;  // True when syncable data (contacts, groups) changed
  isLocalDirty: boolean; // True when local data (statuses, messages) changed

  // Loading state
  isLoading: boolean;
  isSaving: boolean;

  // Actions
  loadVault: (data: VaultData, revisionsByIdentityId?: Record<string, number>, syncPending?: boolean) => void;
  clearVault: () => void;

  setIdentityRevision: (identityId: string, revision: number) => void;

  // Contact management
  addContact: (contact: VaultContact) => void;
  updateContact: (contactId: string, updates: Partial<VaultContact>) => void;
  deleteContact: (contactId: string) => void;
  getContact: (contactId: string) => VaultContact | null;
  findContactByIdentityId: (identityId: string) => VaultContact | null;

  // Identity management within contacts
  addIdentityToContact: (contactId: string, identity: VaultContactIdentity) => void;
  removeIdentityFromContact: (contactId: string, serverId: string, fingerprint: string) => void;
  updateIdentityInContact: (contactId: string, serverId: string, fingerprint: string, updates: Partial<VaultContactIdentity>) => void;
  removeServerIdentitiesFromAllContacts: (serverId: string) => void;

  // Group management
  addGroup: (group: VaultContactGroup) => void;
  updateGroup: (groupId: string, updates: Partial<VaultContactGroup>) => void;
  deleteGroup: (groupId: string) => void;

  setPreference: (key: string, value: unknown) => void;

  // Photo gallery management
  addPhotoToGallery: (photo: NonNullable<VaultData['photoGallery']>['photos'][number]) => void;
  updatePhotoServerBlobId: (localId: string, serverId: string, blobId: string) => void;
  removePhotoFromGallery: (localId: string) => void;
  setCircleAvatar: (identityId: string, localId: string | null) => void;
  getCircleAvatarLocalId: (identityId: string) => string | null;
  getPhotoByLocalId: (localId: string) => NonNullable<VaultData['photoGallery']>['photos'][number] | null;

  // Circle display name management (synced across user's devices)
  setCircleDisplayName: (identityId: string, name: string | null) => void;
  getCircleDisplayName: (identityId: string) => string | null;
  setCircleOwnerIdentityId: (identityId: string, ownerIdentityId: string | null) => void;
  getCircleOwnerIdentityId: (identityId: string) => string | null;
  setCircleOwnerIdentityName: (identityId: string, ownerIdentityName: string | null) => void;
  getCircleOwnerIdentityName: (identityId: string) => string | null;

  // Status management (local data - not synced)
  updateContactStatuses: (statuses: Record<string, {
    statusText: string | null;
    statusUpdatedAt: string | null;
    avatarBlobId?: string | null;
    presence?: {
      visibility?: 'visible' | 'hidden';
      isOnline: boolean;
      lastSeenAt: string | null;
      onlineUntil: string | null;
    };
  }>) => void;
  getContactStatus: (identityId: string) => {
    statusText: string | null;
    statusUpdatedAt: string | null;
    avatarBlobId?: string | null;
    presence?: {
      visibility?: 'visible' | 'hidden';
      isOnline: boolean;
      lastSeenAt: string | null;
      onlineUntil: string | null;
    };
    cachedAt: string;
  } | null;

  // Server registration tracking
  updateServerRegistration: (serverId: string, registrationDate: string) => void;

  markSyncClean: () => void;
  markLocalClean: () => void;
  markAllClean: () => void;
  markSyncDirty: () => void;
  markLocalDirty: () => void;

  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
}

export const useVaultStore = create<VaultState>((set, get) => ({
  vaultData: null,
  revisionsByIdentityId: {},
  isSyncDirty: false,
  isLocalDirty: false,

  isLoading: false,
  isSaving: false,

  loadVault: (data, revisionsByIdentityId, syncPending = false) => set(() => {
    // Normalize contact serverIds to origin form to avoid collisions and mismatched lookups.
    const localIdentities = Object.values(useAuthStore.getState().identities);
    const hostToOrigin = new Map<string, string>();
    for (const i of localIdentities) {
      const origin = serverKeyFromServerUrl(i.serverUrl);
      const host = hostFromServerUrl(i.serverUrl);
      if (origin && host) hostToOrigin.set(host, origin);
    }

    const normalizeServerId = (serverId: string, serverUrl?: string) => {
      const byUrl = serverKeyFromServerUrl(serverUrl);
      if (byUrl) return byUrl;
      const mapped = hostToOrigin.get(serverId);
      return mapped || serverId;
    };

    const normalizedVault: VaultData = {
      ...data,
      contacts: Object.fromEntries(
        Object.entries(data.contacts || {}).map(([contactId, contact]) => {
          const identities = (contact.identities || []).map((id) => ({
            ...id,
            serverId: normalizeServerId(id.serverId, id.serverUrl)
          }));
          return [contactId, { ...contact, identities }];
        })
      )
    };

    // Identity IDs are stable crypto-derived values — no normalization needed.
    const nextRevisions: Record<string, number> = { ...(revisionsByIdentityId || {}) };

    schedulePersist(normalizedVault, nextRevisions, syncPending);
    syncAllContactsToBridge(Object.values(normalizedVault.contacts));
    return {
      vaultData: normalizedVault,
      revisionsByIdentityId: nextRevisions,
      isSyncDirty: syncPending,
      isLocalDirty: false
    };
  }),

  clearVault: () => {
    void clearLocalVault();
    set({
      vaultData: null,
      revisionsByIdentityId: {},
      isSyncDirty: false,
      isLocalDirty: false
    });
  },

  setIdentityRevision: (identityId, revision) => set((state) => {
    const next = {
      ...state.revisionsByIdentityId,
      [identityId]: revision
    };
    schedulePersist(state.vaultData, next, state.isSyncDirty);
    return { revisionsByIdentityId: next };
  }),

  addContact: (contact) => set((state) => {
    if (!state.vaultData) {
      // Initialize vault if not exists
      const nextVault: VaultData = {
          version: 1,
          contacts: { [contact.contactId]: contact },
          groups: {},
          meta: { updatedAt: new Date().toISOString() }
        };
      schedulePersist(nextVault, state.revisionsByIdentityId, true);
      syncContactToBridge(contact);
      return { vaultData: nextVault, isSyncDirty: true };
    }

    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: {
          ...state.vaultData.contacts,
          [contact.contactId]: contact
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    syncContactToBridge(contact);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  findContactByIdentityId: (identityId) => {
    const state = get();
    if (!state.vaultData) return null;

    for (const contact of Object.values(state.vaultData.contacts)) {
      const hasIdentity = contact.identities.some(
        (id) => id.identityId === identityId
      );
      if (hasIdentity) {
        return contact;
      }
    }
    return null;
  },

  addIdentityToContact: (contactId, identity) => set((state) => {
    if (!state.vaultData || !state.vaultData.contacts[contactId]) {
      return state;
    }

    const contact = state.vaultData.contacts[contactId];

    // Check if identity already exists
    const existingIndex = contact.identities.findIndex(
      (id) => id.serverId === identity.serverId && id.identityId === identity.identityId
    );

    let updatedIdentities;
    if (existingIndex >= 0) {
      // Update existing identity
      updatedIdentities = [...contact.identities];
      updatedIdentities[existingIndex] = identity;
    } else {
      // Add new identity
      updatedIdentities = [...contact.identities, identity];
    }

    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: {
          ...state.vaultData.contacts,
          [contactId]: {
            ...contact,
            identities: updatedIdentities
          }
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    const updatedContactAfterAddIdentity = { ...contact, identities: updatedIdentities };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    syncContactToBridge(updatedContactAfterAddIdentity);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  removeIdentityFromContact: (contactId, serverId, fingerprint) => set((state) => {
    if (!state.vaultData || !state.vaultData.contacts[contactId]) {
      return state;
    }

    const contact = state.vaultData.contacts[contactId];
    const updatedIdentities = contact.identities.filter(
      (id) => !(id.serverId === serverId && id.identityId === fingerprint)
    );
    const updatedContactAfterRemove = { ...contact, identities: updatedIdentities };

    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: {
          ...state.vaultData.contacts,
          [contactId]: updatedContactAfterRemove
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    syncContactToBridge(updatedContactAfterRemove);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  updateIdentityInContact: (contactId, serverId, fingerprint, updates) => set((state) => {
    if (!state.vaultData || !state.vaultData.contacts[contactId]) {
      return state;
    }

    const contact = state.vaultData.contacts[contactId];
    const identityIndex = contact.identities.findIndex(
      (id) => id.serverId === serverId && id.identityId === fingerprint
    );

    if (identityIndex < 0) {
      return state;
    }

    const updatedIdentities = [...contact.identities];
    updatedIdentities[identityIndex] = {
      ...updatedIdentities[identityIndex],
      ...updates
    };
    const updatedContactAfterUpdateIdentity = { ...contact, identities: updatedIdentities };

    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: {
          ...state.vaultData.contacts,
          [contactId]: updatedContactAfterUpdateIdentity
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    syncContactToBridge(updatedContactAfterUpdateIdentity);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  removeServerIdentitiesFromAllContacts: (serverId) => set((state) => {
    if (!state.vaultData) return state;

    const updatedContacts: Record<string, VaultContact> = {};
    let hasChanges = false;

    // Process each contact
    for (const [contactId, contact] of Object.entries(state.vaultData.contacts)) {
      const filteredIdentities = contact.identities.filter(
        (identity) => identity.serverId !== serverId
      );

      // Only include contacts that still have at least one identity
      if (filteredIdentities.length > 0) {
        updatedContacts[contactId] = {
          ...contact,
          identities: filteredIdentities
        };

        if (filteredIdentities.length !== contact.identities.length) {
          hasChanges = true;
        }
      } else {
        // Contact has no identities left, remove it entirely
        hasChanges = true;
      }
    }

    if (!hasChanges) {
      return state;
    }

    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: updatedContacts,
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    syncAllContactsToBridge(Object.values(updatedContacts));
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  updateContact: (contactId, updates) => set((state) => {
    if (!state.vaultData || !state.vaultData.contacts[contactId]) {
      return state;
    }

    const updatedContact = { ...state.vaultData.contacts[contactId], ...updates };
    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: {
          ...state.vaultData.contacts,
          [contactId]: updatedContact
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    syncContactToBridge(updatedContact);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  deleteContact: (contactId) => set((state) => {
    if (!state.vaultData) return state;

    const remainingContacts = { ...state.vaultData.contacts };
    delete remainingContacts[contactId];

    const nextVault: VaultData = {
        ...state.vaultData,
        contacts: remainingContacts,
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    deleteContactFromBridge(contactId);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  getContact: (contactId) => {
    const state = get();
    if (!state.vaultData) return null;
    return state.vaultData.contacts[contactId] || null;
  },

  addGroup: (group) => set((state) => {
    if (!state.vaultData) return state;

    const nextVault: VaultData = {
        ...state.vaultData,
        groups: {
          ...state.vaultData.groups,
          [group.groupId]: group
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  updateGroup: (groupId, updates) => set((state) => {
    if (!state.vaultData || !state.vaultData.groups[groupId]) {
      return state;
    }

    const nextVault: VaultData = {
        ...state.vaultData,
        groups: {
          ...state.vaultData.groups,
          [groupId]: {
            ...state.vaultData.groups[groupId],
            ...updates
          }
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  deleteGroup: (groupId) => set((state) => {
    if (!state.vaultData) return state;

    const remainingGroups = { ...state.vaultData.groups };
    delete remainingGroups[groupId];

    const nextVault: VaultData = {
        ...state.vaultData,
        groups: remainingGroups,
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  setPreference: (key, value) => set((state) => {
    if (!state.vaultData) return state;

    const preferences = { ...(state.vaultData.preferences || {}) };
    if (typeof value === 'undefined') delete preferences[key];
    else preferences[key] = value;
    const nextVault: VaultData = {
        ...state.vaultData,
        preferences,
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  addPhotoToGallery: (photo) => set((state) => {
    if (!state.vaultData) return state;
    const existing = state.vaultData.photoGallery?.photos || [];
    const nextVault: VaultData = {
      ...state.vaultData,
      photoGallery: { photos: [...existing, photo] },
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  updatePhotoServerBlobId: (localId, serverId, blobId) => set((state) => {
    if (!state.vaultData) return state;
    const photos = (state.vaultData.photoGallery?.photos || []).map((p) =>
      p.localId === localId
        ? { ...p, serverBlobIds: { ...p.serverBlobIds, [serverId]: blobId } }
        : p
    );
    const nextVault: VaultData = {
      ...state.vaultData,
      photoGallery: { photos },
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  removePhotoFromGallery: (localId) => set((state) => {
    if (!state.vaultData) return state;
    const photos = (state.vaultData.photoGallery?.photos || []).filter((p) => p.localId !== localId);
    const nextVault: VaultData = {
      ...state.vaultData,
      photoGallery: { photos },
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  setCircleAvatar: (identityId, localId) => set((state) => {
    if (!state.vaultData) return state;
    const next = { ...(state.vaultData.circleAvatarLocalIds || {}) };
    if (localId) next[identityId] = localId;
    else delete next[identityId];
    const nextVault: VaultData = {
      ...state.vaultData,
      circleAvatarLocalIds: next,
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  getCircleAvatarLocalId: (identityId) => {
    const state = get();
    return state.vaultData?.circleAvatarLocalIds?.[identityId] ?? null;
  },

  getPhotoByLocalId: (localId) => {
    const state = get();
    return state.vaultData?.photoGallery?.photos.find((p) => p.localId === localId) ?? null;
  },

  setCircleDisplayName: (identityId, name) => set((state) => {
    if (!state.vaultData) return state;
    const next = { ...(state.vaultData.circleDisplayNames || {}) };
    const trimmed = (name || '').trim();
    if (trimmed) next[identityId] = trimmed;
    else delete next[identityId];
    const nextVault: VaultData = {
      ...state.vaultData,
      circleDisplayNames: next,
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  getCircleDisplayName: (identityId) => {
    const state = get();
    return state.vaultData?.circleDisplayNames?.[identityId]?.trim() || null;
  },

  setCircleOwnerIdentityId: (identityId, ownerIdentityId) => set((state) => {
    if (!state.vaultData) return state;
    const next = { ...(state.vaultData.circleOwnerIdentityIds || {}) };
    if (ownerIdentityId) next[identityId] = ownerIdentityId;
    else delete next[identityId];
    const nextVault: VaultData = {
      ...state.vaultData,
      circleOwnerIdentityIds: next,
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  getCircleOwnerIdentityId: (identityId) => {
    const state = get();
    return state.vaultData?.circleOwnerIdentityIds?.[identityId] ?? null;
  },

  setCircleOwnerIdentityName: (identityId, ownerIdentityName) => set((state) => {
    if (!state.vaultData) return state;
    const next = { ...(state.vaultData.circleOwnerIdentityNames || {}) };
    const trimmed = (ownerIdentityName || '').trim();
    if (trimmed) next[identityId] = trimmed;
    else delete next[identityId];
    const nextVault: VaultData = {
      ...state.vaultData,
      circleOwnerIdentityNames: next,
      meta: { ...state.vaultData.meta, updatedAt: new Date().toISOString() }
    };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  getCircleOwnerIdentityName: (identityId) => {
    const state = get();
    return state.vaultData?.circleOwnerIdentityNames?.[identityId]?.trim() || null;
  },

  updateContactStatuses: (statuses) => set((state) => {
    if (!state.vaultData) return state;

    const now = new Date().toISOString();
    const currentStatuses = state.vaultData.localData?.contactStatuses || {};
    const updatedStatuses = { ...currentStatuses };

    for (const [identityId, statusData] of Object.entries(statuses)) {
      updatedStatuses[identityId] = {
        statusText: statusData.statusText,
        statusUpdatedAt: statusData.statusUpdatedAt,
        avatarBlobId: statusData.avatarBlobId ?? currentStatuses[identityId]?.avatarBlobId ?? null,
        presence: statusData.presence ?? currentStatuses[identityId]?.presence,
        cachedAt: now
      };
    }

    const nextVault: VaultData = {
        ...state.vaultData,
        localData: {
          ...state.vaultData.localData,
          contactStatuses: updatedStatuses
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: now
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, state.isSyncDirty);
    return { vaultData: nextVault, isLocalDirty: true };
  }),

  getContactStatus: (identityId) => {
    const state = get();
    if (!state.vaultData?.localData?.contactStatuses) return null;
    return state.vaultData.localData.contactStatuses[identityId] || null;
  },

  updateServerRegistration: (serverId, registrationDate) => set((state) => {
    if (!state.vaultData) return state;

    const nextVault: VaultData = {
        ...state.vaultData,
        serverRegistrations: {
          ...(state.vaultData.serverRegistrations || {}),
          [serverId]: registrationDate
        },
        meta: {
          ...state.vaultData.meta,
          updatedAt: new Date().toISOString()
        }
      };
    schedulePersist(nextVault, state.revisionsByIdentityId, true);
    return { vaultData: nextVault, isSyncDirty: true };
  }),

  markSyncClean: () => set((state) => {
    schedulePersist(state.vaultData, state.revisionsByIdentityId, false);
    return { isSyncDirty: false };
  }),
  markLocalClean: () => set({ isLocalDirty: false }),
  markAllClean: () => set((state) => {
    schedulePersist(state.vaultData, state.revisionsByIdentityId, false);
    return { isSyncDirty: false, isLocalDirty: false };
  }),
  markSyncDirty: () => set((state) => {
    schedulePersist(state.vaultData, state.revisionsByIdentityId, true);
    return { isSyncDirty: true };
  }),
  markLocalDirty: () => set({ isLocalDirty: true }),

  setLoading: (loading) => set({ isLoading: loading }),
  setSaving: (saving) => set({ isSaving: saving })
}));

// Selectors
export const selectContacts = (state: VaultState): VaultContact[] => {
  if (!state.vaultData) return [];
  return Object.values(state.vaultData.contacts);
};
